/**
 * Single-key shortcuts for Needs you and the draft's review (WS-22), as the
 * triage tools have them: a key per answer, Enter to record and move on.
 * Ignored while typing in a field, or once a field has handled the key —
 * except Escape, and ⌘↵ / Ctrl+↵ where a map asks for it (`Mod+Enter`) —
 * and inside an open dialog (its keys are its own). The latest handlers are
 * used without re-binding the listener.
 */

import { useEffect, useRef } from 'react';

/** True when a key press belongs to a field the person is typing in. */
export function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable ||
    target.getAttribute('role') === 'textbox' ||
    target.getAttribute('role') === 'combobox'
  );
}

/** True while a modal dialog is open, or when a key press comes from inside
 * one. */
export function inDialog(target: EventTarget | null): boolean {
  return (
    (target instanceof Element &&
      target.closest('[role="dialog"], [aria-modal="true"]') !== null) ||
    document.querySelector('[role="dialog"][aria-modal="true"]') !== null
  );
}

export type KeyMap = Record<string, (event: KeyboardEvent) => void>;

export function useWorkbenchKeys(map: KeyMap, enabled = true) {
  const latest = useRef(map);
  latest.current = map;
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if (inDialog(event.target)) {
        return;
      }
      // ⌘↵ saves even from a field that took the Enter itself (the draft's
      // paragraph editor finishes on it).
      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        const handler = latest.current['Mod+Enter'];
        if (handler) {
          event.preventDefault();
          handler(event);
        }
        return;
      }
      if (event.defaultPrevented) {
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      if (typing(event.target) && event.key !== 'Escape') {
        return;
      }
      const handler = latest.current[event.key];
      if (handler) {
        event.preventDefault();
        handler(event);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}
